import { useState, useMemo } from 'react';
import type { Table, MenuItem, MenuCategory } from '@/types/common';
import { formatCurrency } from '@/utils/format';
import { getCategoryIcon } from '@/utils/menuIcons';

interface MenuSectionProps {
  tables: Table[];
  categories: MenuCategory[];
  selectedTable: string;
  onSelectTable: (tableId: string) => void;
  onAddItem: (item: MenuItem) => void;
  saving: boolean;
  orderId?: string;
}

const CATEGORY_ORDER = [
  'Starters',
  'Main Courses',
  'Desserts',
  'Wine',
  'Beer',
  'Cocktails',
  'Spirits',
  'Champagne',
  'Soft Drinks',
  'Hot Drinks'
];

export default function MenuSection({
  tables,
  categories,
  selectedTable,
  onSelectTable,
  onAddItem,
  saving,
  orderId
}: MenuSectionProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    let items = selectedCategory === 'all' 
      ? categories.flatMap(c => c.items) 
      : categories.find(c => c.category.id === selectedCategory)?.items || [];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.subcategory?.name.toLowerCase().includes(query)
      );
    }

    return items;
  }, [categories, selectedCategory, searchQuery]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const aCategoryIndex = CATEGORY_ORDER.indexOf(a.category?.name || '');
      const bCategoryIndex = CATEGORY_ORDER.indexOf(b.category?.name || '');
      
      if (aCategoryIndex !== bCategoryIndex) {
        if (aCategoryIndex === -1) return 1;
        if (bCategoryIndex === -1) return -1;
        return aCategoryIndex - bCategoryIndex;
      }
      
      if (a.subcategory && b.subcategory) {
        return a.subcategory.name.localeCompare(b.subcategory.name);
      }
      if (a.subcategory) return -1;
      if (b.subcategory) return 1;
      
      return a.name.localeCompare(b.name);
    });
  }, [filteredItems]);

  return (
    <div className="flex flex-col h-full">
      {/* Controls Section - Fixed at top */}
      <div className="bg-white/80 dark:bg-dark-bg/80 backdrop-blur-sm border-b border-gray-200 dark:border-dark-border">
        <div className="px-4 py-3 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
            {/* Table selection or display */}
            <div className="w-full sm:w-64">
              {orderId ? (
                <div className="text-base text-gray-900 dark:text-dark-text-primary">
                  <span className="font-medium">Table #{tables.find(t => t.id === selectedTable)?.number}</span>
                </div>
              ) : (
                <>
                  <label htmlFor="table" className="sr-only">
                    Select table
                  </label>
                  <select
                    id="table"
                    value={selectedTable}
                    onChange={(e) => onSelectTable(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-bg-light"
                  >
                    <option value="">Select a table</option>
                    {(() => {
                      const availableTables = tables.filter(table => 
                        table.status === 'available' || table.id === selectedTable
                      );
                      
                      if (availableTables.length === 0) {
                        return <option value="" disabled>No tables available</option>;
                      }
                      
                      return availableTables.map((table) => (
                        <option key={table.id} value={table.id}>
                          Table #{table.number}{table.id === selectedTable ? ' (Selected)' : ''}
                        </option>
                      ));
                    })()}
                  </select>
                </>
              )}
            </div>

            {/* Search bar */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg leading-5 bg-white dark:bg-dark-bg-light text-gray-900 dark:text-dark-text-primary placeholder-gray-500 dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Category filters */}
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500">
            <nav className="flex space-x-4 min-w-max">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`${
                  selectedCategory === 'all'
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 dark:text-dark-text-secondary hover:text-gray-700 dark:hover:text-dark-text-primary'
                } px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap flex items-center`}
              >
                <i className="fas fa-utensils mr-2" />
                All Items
              </button>
              {categories
                .sort((a, b) => {
                  const aIndex = CATEGORY_ORDER.indexOf(a.category.name);
                  const bIndex = CATEGORY_ORDER.indexOf(b.category.name);
                  if (aIndex === -1 && bIndex === -1) return a.category.name.localeCompare(b.category.name);
                  if (aIndex === -1) return 1;
                  if (bIndex === -1) return -1;
                  return aIndex - bIndex;
                })
                .map(({ category }) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`${
                      selectedCategory === category.id
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-500 dark:text-dark-text-secondary hover:text-gray-700 dark:hover:text-dark-text-primary'
                    } px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap flex items-center`}
                  >
                    <i className={`${getCategoryIcon(category.name)} mr-2`} />
                    {category.name}
                  </button>
                ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Menu items grid - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4">
            {sortedItems.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12">
                <div className="bg-gray-100 dark:bg-dark-bg-lighter rounded-full p-4 mb-4">
                  <i className="fas fa-search text-3xl text-gray-400" />
                </div>
                <h3 className="text-base font-medium text-gray-900 dark:text-dark-text-primary mb-1">
                  No items found
                </h3>
                <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
                  {searchQuery ? 'Try adjusting your search' : 'No items in this category'}
                </p>
              </div>
            ) : (
              <>
                {selectedCategory === 'all' ? (
                  CATEGORY_ORDER
                    .concat(
                      Object.keys(
                        sortedItems.reduce((acc, item) => {
                          const categoryName = item.category?.name || 'Uncategorized';
                          acc[categoryName] = true;
                          return acc;
                        }, {} as Record<string, boolean>)
                      ).filter(cat => !CATEGORY_ORDER.includes(cat))
                    )
                    .map(categoryName => {
                      const items = sortedItems.filter(item => (item.category?.name || 'Uncategorized') === categoryName);
                      if (items.length === 0) return null;
                      
                      return (
                        <div key={categoryName} className="col-span-full">
                          <div className="flex items-center mb-4">
                            <i className={`${getCategoryIcon(categoryName)} text-xl mr-3 text-gray-600 dark:text-dark-text-secondary`} />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary">
                              {categoryName}
                            </h2>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                            {items.map((item) => (
                              <button
                                key={item.id}
                                onClick={() => onAddItem(item)}
                                disabled={saving}
                                className="text-left p-4 rounded-lg border border-gray-200 dark:border-dark-border hover:border-primary-500 dark:hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-bg-light/90 backdrop-blur-sm"
                              >
                                <div className="flex items-center mb-2">
                                  <i className={`${getCategoryIcon(item.category?.name || '')} mr-2 text-gray-500 dark:text-dark-text-secondary`} />
                                  <h3 className="font-medium text-lg text-gray-900 dark:text-dark-text-primary">
                                    {item.name}
                                  </h3>
                                </div>
                                
                                {item.description && (
                                  <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-secondary">
                                    {item.description}
                                  </p>
                                )}
                                
                                <div className="mt-2 flex justify-between items-center">
                                  <span className="font-medium text-primary-600 dark:text-primary-400">
                                    {formatCurrency(item.price)}
                                  </span>
                                  {item.subcategory && (
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                                      {item.subcategory.name}
                                    </span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })
                    .filter(Boolean)
                ) : (
                  sortedItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => onAddItem(item)}
                      disabled={saving}
                      className="text-left p-4 rounded-lg border border-gray-200 dark:border-dark-border hover:border-primary-500 dark:hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-bg-light/90 backdrop-blur-sm"
                    >
                      <div className="flex items-center mb-2">
                        <i className={`${getCategoryIcon(item.category?.name || '')} mr-2 text-gray-500 dark:text-dark-text-secondary`} />
                        <h3 className="font-medium text-lg text-gray-900 dark:text-dark-text-primary">
                          {item.name}
                        </h3>
                      </div>
                      
                      {item.description && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-secondary">
                          {item.description}
                        </p>
                      )}
                      
                      <div className="mt-2 flex justify-between items-center">
                        <span className="font-medium text-primary-600 dark:text-primary-400">
                          {formatCurrency(item.price)}
                        </span>
                        {item.subcategory && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                            {item.subcategory.name}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}